import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import ExitToAppOutlined from '@mui/icons-material/ExitToAppOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

import { DailyProvider, useCallObject } from '@daily-co/daily-react';
import {
  DailyEvent,
  DailyEventObject,
  DailyEventObjectParticipant,
  DailyParticipant,
} from '@daily-co/daily-js';

import { DAILY_STATE } from '../../global/constants/daily';

import { useBookingsContext } from '../../contexts/BookingsContext';
import { useSessionContext } from '../../contexts/SessionContext';
import useSession from '../../hooks/useSession';

import CustomButton from '../../components/CustomButton';
import CustomModal from '../../components/CustomModal';
import InformationModal from '../../components/InformationModal';
import SliderBar from '../../components/SliderBar';
import Spinner from '../../components/Spinner';
import Room from './Room';
import Panels from './Panels';

import { useInspectorStyles } from './styles';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion,
  Modal,
} from '@mui/material';

import dayjs from 'dayjs';
import { MOBILE_STATUS_MESSAGES } from '../../utils/messaging/mediaStatus';
import { ExpandMore, Sick } from '@mui/icons-material';

const VirtualSession = () => {
  const styles = useInspectorStyles();
  const navigate = useNavigate();
  const { fetchInspectorRoomToken } = useSession();

  const [appState, setAppState] = useState<number>(DAILY_STATE.IDLE);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string>('');
  const [meetingToken, setMeetingToken] = useState<string | null>(null);

  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteStatsRef = useRef<any>();
  const [roomWidth, setRoomWidth] = useState<number>(50); //get this from user properties
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const { selectedBooking, refetchBookings } = useBookingsContext();

  const pathParts = window.location.pathname.split('/');
  const bookingSessionCode = pathParts[pathParts.length - 1];

  const {
    selectedImage,
    setSelectedImage,
    setSession,
    resetSession,
    remoteParticipant,
    setRemoteParticipant,
    setSipSessionId,
    setTranscribeState,
    sipSessionId,
  } = useSessionContext();

  const { sipHangup } = useSession();

  const callObject = useCallObject({});

  const { mediaStatus, setPingMediaStatus } = useSession(
    selectedBooking?.booking_id,
  );
  const [mediaCheckModalOpen, setMediaCheckModalOpen] = useState(false);

  const handleClose = () => {
    setMediaCheckModalOpen(false);
  };

  useEffect(() => {
    setPingMediaStatus(1);
  }, []);

  const remoteSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!callObject) return;

    const interval = setInterval(() => {
      const participants = callObject.participants();
      Object.entries(participants).forEach(([id, participant]) => {
        if (
          id === 'local' ||
          participant.local ||
          participant.participantType === 'sip-dial-out' ||
          participant.user_id === 'ai-agent' ||
          remoteSessionIdRef.current === participant.session_id
        )
          return;

        console.log('::::setRemoteParticipant', participant);
        remoteSessionIdRef.current = participant.session_id;
        setRemoteParticipant(participant);
      });
    }, 2000); //this is a hack to get the remote participant if all else fails

    return () => {
      clearInterval(interval);
    };
  }, [callObject]);

  useEffect(() => {
    if (!callObject) return;

    const updateParticipants = (e: DailyEventObject = undefined) => {
      console.log('::::updateParticipants e: ', e);
      const participants = callObject.participants() || {};
      console.log('::::participants:', participants);

      setRemoteParticipant(
        Object.values(participants).find(
          (participant) =>
            participant.participantType !== 'sip-dial-out' &&
            participant.local === false,
        ),
      );

      setSipSessionId(
        Object.values(participants).find(
          (participant) => participant.participantType === 'sip-dial-out',
        )?.session_id || '',
      );

      if (e?.action === 'participant-left') {
        setSelectedImage(null);
      }
      /*
      const participant = e?.participant;
      switch (e.action) {
        case 'participant-joined':
          if (participant?.userId === 'ai-agent')
            setAgentParticipantId(participant.user_id);
          else if (participant?.participantType === 'sip-dial-out')
            setSipSessionId(participant.session_id);
          else if (!participant?.local) {
            setHomeownerSessionId(participant.session_id);
          }
          break;

        case 'participant-left':
          if (participant?.userId === 'ai-agent') setAgentParticipantId('');
          else if (participant?.participantType === 'sip-dial-out')
            setSipSessionId('');
          else if (!participant?.local) setHomeownerSessionId('');
          else {
            console.log(
              ':::::::::::::participants:',
              callObject.participants(),
            );
          }

          break;

        case 'participant-updated':
          console.log(
            '::::::::::::participant-updated participant.user_id: ',
            participant.user_id,
          );
          break;
      }
      */
    };

    updateParticipants();
    callObject.on('participant-joined', updateParticipants);
    callObject.on('participant-left', updateParticipants);
    callObject.on('participant-updated', updateParticipants);

    return () => {
      callObject.off('participant-joined', updateParticipants);
      callObject.off('participant-left', updateParticipants);
      callObject.off('participant-updated', updateParticipants);
    };
  }, [callObject]);

  useEffect(() => {
    const hangup = async () => {
      console.log('::::hangup sipSessionId: ', sipSessionId, remoteParticipant);
      const resp = await sipHangup({
        session_id: sipSessionId,
        room_name: roomName,
      });

      if (resp.status === 'sent') {
        setSipSessionId('');
      }
    };

    if (remoteParticipant && sipSessionId) hangup();
  }, [remoteParticipant, sipSessionId]);

  useEffect(() => {
    console.log('::::remoteParticipant change: ', remoteParticipant);
  }, [remoteParticipant]);

  useEffect(() => {
    if (!callObject) return;

    const handleEvent = (event: any) => {
      console.log('Daily event:', event);

      switch (event.action) {
        case 'joined-meeting':
          setAppState(DAILY_STATE.JOINED);
          break;

        case 'left-meeting':
          setAppState(DAILY_STATE.LEFT);
          break;

        case 'error':
          console.log('::::setAppState Error event');
          setAppState(DAILY_STATE.ERROR);
          break;

        case 'transcription-message':
          console.log('::::transcription-message:', event);
          break;
      }
    };

    const events: DailyEvent[] = [
      'joined-meeting',
      'left-meeting',
      'error',
      'transcription-message',
    ];

    events.forEach((type) => callObject.on(type, handleEvent));

    return () => {
      events.forEach((type) => callObject.off(type, handleEvent));
    };
  }, [callObject]);

  useEffect(() => {
    console.log('::::callObject: ', callObject);
    if (callObject && roomUrl && meetingToken) {
      handleEnterRoom(roomUrl, meetingToken);
    }
  }, [callObject, roomUrl, meetingToken]);

  const handleOpenExitModal = useCallback(() => {
    setIsExitModalOpen(true);
  }, []);

  const handleEnterRoom = async (url: string, token: string) => {
    if (!callObject) {
      console.log('::::handleEnterRoom callObject is null');
      return;
    }

    console.log('do init...');
    setAppState(DAILY_STATE.JOINING);

    try {
      const sendSettings = await callObject.updateSendSettings({
        video: {
          allowAdaptiveLayers: true,
        },
      });
      console.log('::::callObject.updateSendSettings', sendSettings);

      const result = await callObject.join({ url, token });
      setAppState(DAILY_STATE.JOINED);
      console.log('::::init callObject.join result: ', result);
      console.log(
        '::::init callObject.getSendSettings: ',
        callObject.getSendSettings(),
      );
      const receiveSettings = await callObject.getReceiveSettings();
      console.log('::::init callObject.getReceiveSettings: ', receiveSettings);
    } catch (error) {
      setAppState(DAILY_STATE.ERROR);
      console.log('::::init callObject.join error: ', error, callObject);
    }
  };

  useEffect(() => {
    if (
      !selectedBooking ||
      selectedBooking.booking_session_code !== bookingSessionCode ||
      (roomUrl && meetingToken)
    ) {
      console.log(
        '::::useEffect selectedBooking.booking_session_code: ',
        selectedBooking?.booking_session_code,
      );
      console.log('::::useEffect bookingSessionCode: ', bookingSessionCode);
      console.log('::::useEffect roomUrl: ', roomUrl);
      console.log('::::useEffect meetingToken: ', meetingToken);
      return;
    }
    const initializeRoom = async () => {
      console.log('::::initializeRoom...');
      try {
        if (!selectedBooking) throw new Error('Booking must be selected.');

        const data = await fetchInspectorRoomToken(
          selectedBooking.booking_session_code,
        );

        setSession(selectedBooking, data);
        setRoomUrl(data.room.url);
        setRoomName(data.room.name);
        setMeetingToken(data.meeting_token);

        console.log('::::room_url: ', data.room.url);
        console.log('::::room_name: ', data.room.name);
        console.log('::::meetingToken: ', data.meeting_token);

        console.log('ready to join room...');
        //handleEnterRoom(data.room.url, data.meeting_token);
      } catch (error) {
        setAppState(DAILY_STATE.ERROR);
        console.error('::::setAppState Error joining inspection:', error);
      }
    };

    initializeRoom();
    window.addEventListener('beforeunload', handleExitRoom);
    window.addEventListener('popstate', handleExitRoom);
    //window.addEventListener('pagehide', handleExitRoom)

    return () => {
      window.removeEventListener('beforeunload', handleExitRoom);
      window.removeEventListener('popstate', handleExitRoom);
      //window.removeEventListener('pagehide', handleExitRoom)

      console.log('::::handleEnterRoom callObject?.leave()');
      callObject?.leave();
      console.log('::::handleEnterRoom callObject?.destroy()');
      callObject?.destroy();
      resetSession();
    };
  }, [selectedBooking]);

  const handleHangUp = useCallback(() => {
    console.log('::::handleHangUp...', callObject);

    if (!callObject) return;
    const payload = {
      type: 'exit-room',
      date: new Date(),
    };

    console.log(`++++send message: ${JSON.stringify(payload, null, 3)}`);
    try {
      setTranscribeState('off');
      callObject.sendAppMessage(payload, '*');
    } catch (e) {}
  }, [callObject]);

  const handleExitRoom = useCallback(() => {
    console.log(':::handleExitRoom...', callObject);

    if (!callObject) return;
    handleHangUp();

    if (appState === DAILY_STATE.ERROR) {
      callObject.destroy().then(() => {
        setAppState(DAILY_STATE.LEFT);
      });
    } else {
      setAppState(DAILY_STATE.LEAVING);
      callObject.leave().then(() => {
        setAppState(DAILY_STATE.LEFT);
      });
    }
  }, [callObject, appState]);

  useEffect(() => {
    console.log('::::mounting VirtualSessionComponent callObject:', callObject);

    return () => {
      console.log(
        '::::unmounting VirtualSessionComponent callObject:',
        callObject,
      );
    };
  }, []);

  const handleExit = async () => {
    handleExitRoom();
    await refetchBookings();
    navigate('/');
  };

  if (!selectedBooking) {
    navigate('/');
    return <Spinner open={true} />;
  }

  console.log('::::VirtualSession...');
  const component = (
    <>
      <Box height="100vh">
        <CustomModal
          message="Are you sure you wish to end this session?"
          primaryText="Leave Room"
          handlePrimary={handleExit}
          secondaryText="Continue"
          handleSecondary={() => setIsExitModalOpen(false)}
          isModalOpen={isExitModalOpen}
        />

        <Modal
          open={mediaCheckModalOpen}
          onClose={handleClose}
          aria-labelledby="media-status-modal"
          aria-describedby="media-status-details">
          <Box sx={styles.mediaCheckModal}>
            <Typography variant="h6" component="h2">
              Device Check Status
            </Typography>
            {mediaStatus && mediaStatus.length > 0 ? (
              mediaStatus.map((item, index) => (
                <Box key={index} sx={{ mt: 2 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center">
                    <Typography>
                      {MOBILE_STATUS_MESSAGES[
                        item.event as keyof typeof MOBILE_STATUS_MESSAGES
                      ]?.message || item.event}
                    </Typography>
                    <Typography variant="caption">
                      {dayjs(item.timestamp).format('YYYY/MM/DD HH:mm:ss')}
                    </Typography>
                  </Box>
                  {item.data && (
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{ py: 0 }}>
                        <Typography>View Details</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ fontSize: '.75rem', margin: 0 }}>
                          {JSON.stringify(item.data, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              ))
            ) : (
              <Typography>No status information available</Typography>
            )}
          </Box>
        </Modal>

        <Box sx={styles.header}>
          <Box display="flex">
            <Typography fontWeight={500} fontSize=".8125rem">
              HOMEOWNER:
              <Typography sx={styles.title} variant="caption">
                {selectedBooking?.title}
              </Typography>
            </Typography>

            {remoteParticipant ? (
              <Box sx={styles.participant}>
                <Box sx={styles.circle} />
                <Typography sx={styles.online}>Online</Typography>
                <Box sx={styles.stats} ref={remoteStatsRef} />
              </Box>
            ) : null}
          </Box>

          <Box>
            <CustomButton
              style={styles.exitButton}
              handleClick={remoteParticipant ? handleOpenExitModal : handleExit}
              startIcon={
                <ExitToAppOutlined style={{ fontSize: '1.375rem' }} />
              }>
              Exit
            </CustomButton>
          </Box>
        </Box>

        {appState === DAILY_STATE.ERROR ? (
          <InformationModal message="Unable to establish connection. Please exit the inspection and retry after a few seconds." />
        ) : appState !== DAILY_STATE.JOINED ? (
          <Spinner open={true} />
        ) : (
          <Box height={'calc(100vh - 3.125rem)'} display="flex">
            <Box sx={{ ...styles.roomPanel, width: `${roomWidth}%` }}>
              <Room
                roomName={roomName}
                remoteVideoRef={remoteVideoRef}
                handleHangUp={handleHangUp}
              />
            </Box>
            <SliderBar setWidth={setRoomWidth} />
            <Box sx={{ ...styles.viewsPanel, width: `${100 - roomWidth}%` }}>
              <Panels />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );

  return <DailyProvider callObject={callObject}>{component}</DailyProvider>;
};

export default VirtualSession;
